// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useNavigate } from 'react-router-dom'
import {
  Stethoscope, Flower2, Mail, ArrowRight, Users, MessageSquare, FolderOpen, Smartphone, Scale,
} from 'lucide-react'
import { BackButton }  from '@/components/ui/BackButton'
import { Card }        from '@/components/ui/Card'
import { useWizardStore } from '@/store'
import { ROUTES } from '@/lib/constants/routes'

const WISH_MODULES = [
  {
    title:       'Medische wensen',
    description: 'Behandelgrenzen, reanimatie, wilsverklaring',
    icon:        <Stethoscope className="h-5 w-5" />,
    route:       ROUTES.WISHES_MEDICAL,
    key:         'medical',
  },
  {
    title:       'Uitvaartwensen',
    description: 'Begraving, crematie, plechtigheid, muziek',
    icon:        <Flower2 className="h-5 w-5" />,
    route:       ROUTES.WISHES_FUNERAL,
    key:         'funeral',
  },
  {
    title:       'Persoonlijk afscheid',
    description: 'Afscheidsbrieven, rouwkaart, herinneringen',
    icon:        <Mail className="h-5 w-5" />,
    route:       ROUTES.WISHES_FAREWELL,
    key:         'farewell',
  },
  {
    title:       'Vertrouwenspersoon',
    description: 'Wie krijgt toegang na uw overlijden',
    icon:        <Users className="h-5 w-5" />,
    route:       ROUTES.TRUSTED_PERSON,
    key:         'trusted_person',
  },
  {
    title:       'Berichtenservice',
    description: 'Persoonlijke berichten voor naasten',
    icon:        <MessageSquare className="h-5 w-5" />,
    route:       ROUTES.MESSAGES,
    key:         'messages',
  },
  {
    title:       'Documentwijzer',
    description: 'Locatie van uw belangrijke documenten',
    icon:        <FolderOpen className="h-5 w-5" />,
    route:       ROUTES.DOCUMENTS,
    key:         'documents',
  },
  {
    title:       'Digitale nalatenschap',
    description: 'Accounts, apparaten, crypto',
    icon:        <Smartphone className="h-5 w-5" />,
    route:       ROUTES.AI_GUIDE,
    key:         'digital_estate',
  },
  {
    title:       'Notariële wensen',
    description: 'Executeur, erfgenamen, bezittingen verdelen',
    icon:        <Scale className="h-5 w-5" />,
    route:       ROUTES.NOTARIAL,
    key:         'notarial',
  },
]

export function WishesPage() {
  const navigate = useNavigate()
  const { answers } = useWizardStore()

  return (
    <div className="flex flex-col gap-5 animate-fade-in">
      <BackButton />
      <div>
        <h1 className="text-xl font-semibold text-ink mb-1">Alle onderdelen</h1>
        <p className="text-sm text-ink-3">
          Klik op een onderdeel om uw wensen en gegevens in te vullen of aan te vullen.
        </p>
      </div>

      {/* Persoonlijke aandachtspunten */}
      {(answers.hasChildren || answers.hasPets || answers.hasOwnHome) && (
        <div className="rounded-xl bg-sage-50 border border-sage-200 px-4 py-3">
          <p className="text-xs font-semibold text-sage-700 mb-2">Op basis van uw situatie</p>
          <ul className="text-xs text-sage-600 leading-relaxed space-y-1">
            {answers.hasChildren    && <li>• U heeft kinderen - leg voogdij vast in uw medische wensen of testament</li>}
            {answers.hasPets        && <li>• U heeft huisdieren - noteer de verzorger bij uw documenten</li>}
            {answers.hasOwnHome     && <li>• U heeft een koopwoning - leg de hypotheekgegevens vast</li>}
            {answers.hasBusiness    && <li>• U heeft een onderneming - leg de bedrijfsgegevens en opvolgingswens vast</li>}
            {answers.hasInvestments && <li>• U heeft beleggingen - noteer broker en locatie van toegangsgegevens</li>}
          </ul>
        </div>
      )}

      {/* Module kaarten */}
      <div className="grid gap-3 sm:grid-cols-2">
        {WISH_MODULES.map(mod => (
          <Card key={mod.key} hoverable onClick={() => navigate(mod.route)}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sage-50 text-sage-600 flex-shrink-0">
                {mod.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ink">{mod.title}</p>
                <p className="text-xs text-ink-4 mt-0.5">{mod.description}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-ink-4 flex-shrink-0" />
            </div>
          </Card>
        ))}
      </div>
      <BackButton variant="bottom" />
    </div>
  )
}
